import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ContractService } from '@manager-app/storage/service/contract.service';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ContractModel, SupplierModel } from 'hx-services';
import { from, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { DatePipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
    selector: 'app-contract-search',
    templateUrl: './contract-search.component.html',
    styleUrls: ['./contract-search.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ContractSearchComponent), // replace name as appropriate
            multi: true
        }
    ],
    imports: [NgSelectComponent, FormsModule, NgLabelTemplateDirective, NgOptionTemplateDirective, DatePipe, TranslocoPipe]
})
export class ContractSearchComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  @Input() loadImmediately: boolean = false;
  @Input() supplierId: number;
  @Input() organizationId: number;
  @Output() itemSelect = new EventEmitter<ContractModel>();
  private _selected: number;
  get selected() {
    return this._selected || undefined;
  }

  set selected(val: number | undefined) {
    this._selected = val || undefined;
    if (val) {
      this.getContractById(val);
    } else {
      this.model = undefined;
    }
    this.onChange(val);
  }

  items: ContractModel[] = [];
  model: ContractModel;
  itemLoading = false;
  itemInput$ = new Subject<string>();
  private $destroyed = new Subject<void>();
  selectedItem = false;

  constructor(
    private contractService: ContractService,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['supplierId']) {
      this.loadItems();
    }
  }

  ngOnInit() {
    this.loadItems();
  }

  ngOnDestroy(): void {
    this.$destroyed.next();
    this.$destroyed.complete();
  }

  writeValue(id: number): void {
    this.selected = id;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  onChange(val: any) {
    console.log('onChange called', val);
  }

  onModelChanged(contract?: ContractModel) {
    if (contract) {
      this.selected = contract.id;
      this.itemSelect.emit(contract);
    } else {
      this.selected = undefined;
      this.itemSelect.emit(undefined);
    }
  }

  trackByFn(item: SupplierModel) {
    return item.id;
  }

  compareFn = (item: { id: number }, selected: { id: number }) => item.id === selected.id;

  private async getContractById(id: number): Promise<void> {
    const result = await this.contractService.getContract(id);
    if (result) {
      this.model = result;
    }
  }

  private async loadItems() {
    if (this.loadImmediately) {
      this.itemLoading = true;
      try {
        const result = await this.contractService.getContractList({
          supplierId: this.supplierId,
          organizationId: this.organizationId,
          limit: 1000
        });
        this.items = result.list;
      } finally {
        this.itemLoading = false;
      }
    } else {
      this.itemInput$.pipe(
        takeUntil(this.$destroyed),
        debounceTime(500),
        distinctUntilChanged(),
        filter(term => {
          const isOk = (term ?? '').trim() !== '';
          this.itemLoading = isOk;
          return isOk;
        }),
        switchMap(term => from(this.contractService.getContractList({
          q: term,
          supplierId: this.supplierId,
          limit: 1000
        }).then(pagedResult => pagedResult.list)).pipe(
          catchError(() => of([] as ContractModel[])), // empty list on error
          tap(() => this.itemLoading = false)
        ))
      ).subscribe(searchResult => this.items = searchResult);
    }
  }
}
